import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gaming-portfolio-13/artifacts/54jbluz3_IMG-20260904-WA0003.jpg";

const EFFECTIVE_DATE = "December 9, 2026";

const Section = ({ n, title, children }) => (
  <section className="mb-14" data-testid={`policy-section-${n}`}>
    <div className="flex items-baseline gap-4 mb-4">
      <span className="h-display text-2xl md:text-3xl text-[#4FA8E0]/70">
        {n.toString().padStart(2, "0")}
      </span>
      <h2 className="h-display text-2xl md:text-3xl text-white tracking-[-0.02em]">
        {title}
      </h2>
    </div>
    <div className="text-white/65 leading-relaxed text-[15px] space-y-3 pl-0 md:pl-14">
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy · Andromis Arcade";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App grain relative min-h-screen" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="relative border-b border-white/5 py-8 px-6 md:px-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
            data-testid="privacy-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <img src={LOGO_URL} alt="Andromis Games" className="h-9 w-auto object-contain max-w-[120px]" />
            <span className="hidden md:inline text-xs tracking-[0.3em] uppercase text-white/50">
              Back to home
            </span>
          </Link>
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">Legal</span>
        </div>
      </header>

      {/* Content */}
      <main className="relative py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          {/* Title */}
          <div className="mb-4">
            <div className="eyebrow mb-4 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#4FA8E0]" />
              Privacy Policy
            </div>
            <h1
              className="h-display text-4xl md:text-6xl text-white tracking-[-0.03em] leading-[1]"
              data-testid="privacy-title"
            >
              <span className="chrome-text">Andromis Arcade</span>
              <br />
              <span className="italic font-light text-white/70">Mini Games.</span>
            </h1>
            <p className="mt-6 text-white/55 text-sm">
              Effective date:{" "}
              <span className="text-white/80">{EFFECTIVE_DATE}</span>
              <span className="mx-2 text-white/25">·</span>
              Last updated:{" "}
              <span className="text-white/80">{EFFECTIVE_DATE}</span>
            </p>
          </div>

          <div className="hairline my-10 opacity-40" />

          {/* Intro */}
          <div className="text-white/70 leading-relaxed mb-14 text-base">
            <p className="mb-4">
              This Privacy Policy describes how{" "}
              <span className="text-white">Andromis Games LLC</span> (“Andromis
              Games”, “we”, “us”, or “our”) collects, uses, and shares
              information when you use the mobile application{" "}
              <span className="text-white">Andromis Arcade: Mini Games</span>{" "}
              (the “App”) available on Google Play for Android devices.
            </p>
            <p className="mb-4">
              By downloading, installing, or using the App, you agree to the
              terms of this Privacy Policy. If you do not agree, please do not
              use the App.
            </p>
            <p>
              This Policy is intended to be consistent with the disclosures made
              in the Google Play <span className="text-white">Data safety</span>{" "}
              section for the App.
            </p>
          </div>

          <Section n={1} title="Information We Collect">
            <p>
              Andromis Arcade is designed to keep on-device gameplay local
              wherever possible. The categories of information that may be
              collected, processed, or shared in connection with the App are
              described below.
            </p>
            <p className="text-white/80 mt-4">
              a. Information you provide directly
            </p>
            <p>
              To create an account and use Andromis Arcade, you provide:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                <span className="text-white">Email address</span> and{" "}
                <span className="text-white">password</span> for authentication.
              </li>
              <li>
                <span className="text-white">Display name</span>, and
                optionally <span className="text-white">country</span> and{" "}
                <span className="text-white">favorite game</span> shown on
                your profile and (for display name) on the leaderboard.
              </li>
            </ul>
            <p>
              If you contact us by email, we will also receive the information
              you include in your message.
            </p>
            <p className="text-white/80 mt-4">b. Information collected automatically</p>
            <p>
              When you use the App, certain information may be collected
              automatically by the App itself or by third-party SDKs integrated
              into it, including:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                <span className="text-white">Advertising identifier (AAID)</span>{" "}
                assigned by your device.
              </li>
              <li>
                <span className="text-white">Device and technical information</span>:
                device model, manufacturer, operating system version, device
                language, screen size, app version, and general performance /
                crash information.
              </li>
              <li>
                <span className="text-white">Approximate (coarse) location</span>{" "}
                derived from IP address, used by our advertising provider for
                ad delivery and fraud prevention. The App does not request
                precise (GPS) location.
              </li>
              <li>
                <span className="text-white">Interaction data with ads</span>:
                for example, when an ad is shown, when it is clicked, or
                whether it was closed.
              </li>
            </ul>
            <p className="text-white/80 mt-4">c. Gameplay data linked to your account</p>
            <p>
              Progress, per-game XP, scores, level completion, best times,
              accuracy records, and leaderboard entries are associated with
              your account and stored on our servers so you can access them
              across devices. Some of this data is also cached locally on the
              device you are using for offline performance.
            </p>
          </Section>

          <Section n={2} title="How We Use Information">
            <p>We use the information described above to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide, operate, and maintain the App.</li>
              <li>
                Display advertisements through Google AdMob, including
                personalized ads where you have consented, or non-personalized
                ads where you have not consented or where required by law.
              </li>
              <li>
                Diagnose technical issues, prevent abuse, and improve
                performance and stability.
              </li>
              <li>
                Respond to your questions, support requests, or legal claims.
              </li>
              <li>Comply with applicable laws and enforce our terms.</li>
            </ul>
            <p className="mt-3">
              We do not sell your personal information. We do not use the
              information collected by the App to build a profile of you
              outside the App.
            </p>
          </Section>

          <Section n={3} title="Advertising · Google AdMob">
            <p>
              The App uses the{" "}
              <span className="text-white">Google Mobile Ads SDK</span> (Google
              AdMob) to display advertisements. AdMob is provided by Google LLC
              and its affiliates.
            </p>
            <p>
              AdMob may collect and process information from your device to
              deliver and measure ads, including your advertising identifier
              (AAID), IP address, general (coarse) location derived from your
              IP address, device information, and information about ads shown
              to you and your interactions with them.
            </p>
            <p>
              Depending on your region and your consent choices (for example,
              through a Google-certified consent management interface where
              required by applicable law), AdMob may serve you personalized or
              non-personalized ads. When ads are personalized, information may
              be used by Google and its ad partners to make ads more relevant
              to you. When ads are non-personalized, information is used only
              for limited purposes such as frequency capping, aggregated ad
              reporting, and fighting fraud and abuse.
            </p>
            <p>
              You can learn more about how Google uses information from apps
              that use its services at{" "}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9bd1f5] underline hover:text-white"
              >
                policies.google.com/technologies/partner-sites
              </a>
              . You can review Google’s advertising policies at{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9bd1f5] underline hover:text-white"
              >
                policies.google.com/privacy
              </a>
              .
            </p>
          </Section>

          <Section n={4} title="Your Advertising Identifier & Choices">
            <p>
              Your Android device provides an{" "}
              <span className="text-white">Advertising ID (AAID)</span> that
              can be used by advertising SDKs, including AdMob.
            </p>
            <p>You can, at any time:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                <span className="text-white">Reset your Advertising ID</span>{" "}
                in Android settings (Settings → Google → Ads).
              </li>
              <li>
                <span className="text-white">
                  Delete your Advertising ID
                </span>{" "}
                on supported Android versions to opt out of personalized
                advertising across apps.
              </li>
            </ul>
            <p className="mt-2">
              If you delete your Advertising ID, you will still see ads, but
              they will be non-personalized.
            </p>
          </Section>

          <Section n={5} title="Other Third-Party Services">
            <p>
              The App does not currently integrate third-party analytics,
              social-login, cross-device tracking, or user-account SDKs beyond
              those required by the Google Mobile Ads SDK described above and
              standard platform services provided by Google Play (including
              Google Play Services for delivery, updates, and crash
              diagnostics).
            </p>
            <p>
              If we add additional SDKs in the future that materially change
              our data practices, we will update this Privacy Policy and, where
              required, update our Google Play Data safety declaration.
            </p>
          </Section>

          <Section n={6} title="Sharing of Information">
            <p>We may share information with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="text-white">Google (AdMob)</span> as described
                above, to deliver and measure advertising.
              </li>
              <li>
                <span className="text-white">Service providers</span> that help
                us operate and maintain the App, only to the extent needed for
                them to provide those services.
              </li>
              <li>
                <span className="text-white">
                  Authorities and legal successors
                </span>
                , where required by law, legal process, or in connection with a
                merger, acquisition, or sale of assets.
              </li>
            </ul>
            <p className="mt-3">
              We do not sell your personal information for money, and we do not
              share it for cross-context behavioral advertising outside of the
              third-party services described above.
            </p>
          </Section>

          <Section n={7} title="Data Retention & Security">
            <p>
              Gameplay data is stored locally on your device and is retained
              until you uninstall the App, clear its data, or delete it
              yourself. Advertising and diagnostic data processed by third
              parties are retained according to those providers’ policies,
              including Google’s.
            </p>
            <p>
              We use industry-standard technical and organizational measures
              designed to protect the limited information we handle. However,
              no method of transmission or storage is 100% secure, and we
              cannot guarantee absolute security.
            </p>
          </Section>

          <Section n={8} title="Children’s Privacy">
            <p>
              Andromis Arcade: Mini Games is intended for a{" "}
              <span className="text-white">general audience</span> and is not
              directed to children under the age of 13 (or the equivalent
              minimum age in your jurisdiction).
            </p>
            <p>
              We do not knowingly collect personal information from children
              under 13. If you believe that a child has provided us with
              personal information, please contact us and we will take steps
              to delete that information and, where applicable, terminate the
              child’s use of the App.
            </p>
            <p>
              Where the App is served to users in regions with stricter
              age-based advertising rules, ads served through AdMob are
              configured to comply with those requirements (including
              non-personalized ads for users flagged as under the age of
              consent).
            </p>
          </Section>

          <Section n={9} title="International Users">
            <p>
              The App is operated from the United States. If you access the
              App from outside the United States, please be aware that
              information collected in connection with the App may be
              transferred to, stored, and processed in the United States or
              other jurisdictions where our service providers operate.
            </p>
          </Section>

          <Section n={10} title="Your Privacy Rights">
            <p>
              Depending on where you live, you may have rights regarding the
              personal information we hold about you, including the right to
              access, correct, delete, or restrict the processing of your
              information, and the right to object to certain processing or to
              withdraw consent.
            </p>
            <p>
              To exercise these rights, please contact us at the email address
              below. We will respond within a reasonable time and in accordance
              with applicable law.
            </p>
            <p className="text-white/50">
              <span className="text-white">EEA / UK residents:</span> Our
              lawful basis for processing is your consent (for personalized
              advertising) and our legitimate interests (for maintaining and
              improving the App and serving non-personalized advertising).
            </p>
            <p className="text-white/50">
              <span className="text-white">California residents:</span> You may
              have additional rights under the CCPA/CPRA, including the right
              to know, delete, and opt out of the sale or sharing of personal
              information. We do not sell personal information as defined by
              the CCPA.
            </p>
          </Section>

          <Section n={11} title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes to our practices, the App, or the law. When we make a
              material change, we will update the “Effective date” at the top
              of this Policy and, where appropriate, provide additional
              notice.
            </p>
          </Section>

          <Section n={12} title="Contact Us">
            <p>
              If you have questions or requests regarding this Privacy Policy
              or our privacy practices, contact us at:
            </p>
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="h-display text-white text-lg mb-1">
                Andromis Games LLC
              </div>
              <a
                href="mailto:miky@andromisgames.com"
                className="inline-flex items-center gap-2 text-[#9bd1f5] hover:text-white transition-colors mt-2"
                data-testid="privacy-contact-email"
              >
                <Mail className="w-4 h-4" />
                miky@andromisgames.com
              </a>
              <div className="mt-4">
                <a
                  href="https://andromisgames.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/50 hover:text-white transition-colors text-sm"
                >
                  andromisgames.com
                </a>
              </div>
            </div>
          </Section>
        </div>
      </main>

      <footer className="border-t border-white/5 py-8 px-6 md:px-10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Andromis" className="h-8 w-auto object-contain max-w-[110px]" />
            <span className="text-white/40 text-xs tracking-[0.3em] uppercase">LLC</span>
          </div>
          <div className="text-white/30 text-xs tracking-[0.2em] uppercase">
            © {new Date().getFullYear()} Andromis Games LLC · All rights reserved
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
